// URL-derived org scoping (#460) — the GitHub model: the org lives in the URL.
// `/org/<login>/<section>` scopes everything on that page to that org, so views
// are deep-linkable and multi-tab-safe (two tabs, two orgs). The URL is the
// single client-side source of truth for the acting org:
//
//   - `OrgScope` (the `/org/:org` route wrapper) mirrors the segment into
//     `urlOrg` AND into `activeAccountId` — the signal every account-scoped
//     query already keys on — so `/org/a` and `/org/b` never share cache rows.
//   - `apiFetch` (and the SSE streams) attach the segment as `X-Dz-Account`;
//     the API validates it against the caller's memberships per request.
//   - The org switcher NAVIGATES between `/org/<a>/x` and `/org/<b>/x` instead
//     of mutating the session (`POST /v1/me/active-account` stays server-side
//     for backcompat, out of the normal flow).

import { createSignal } from 'solid-js';

const ORG_PREFIX = '/org/';

/**
 * One raw `/org/<segment>`, URL-decoded — the ONE decode of it (`orgFromPath`,
 * `OrgScope`). `null` when blank or when it does not decode: a malformed escape
 * (`/org/%`) makes `decodeURIComponent` throw `URIError`, and both callers run
 * inside a render, so a thrown segment took the whole shell to the error
 * boundary. A GitHub login is `[A-Za-z0-9-]`, so an undecodable segment can
 * never name an org — it names none, exactly like a bare path.
 */
export function decodeOrgSegment(raw: string): string | null {
  if (raw === '') return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * The org segment of a pathname shaped `/org/<login>[/…]`, URL-decoded.
 * `null` for any other path (admin routes, legacy bare paths, login) and for a
 * segment that does not decode (`decodeOrgSegment`).
 */
export function orgFromPath(pathname: string): string | null {
  if (!pathname.startsWith(ORG_PREFIX)) return null;
  return decodeOrgSegment(pathname.slice(ORG_PREFIX.length).split('/')[0] ?? '');
}

const [urlOrg, setUrlOrg] = createSignal<string | null>(null);

/**
 * The org named by the current URL — set by `OrgScope`, `null` outside
 * `/org/:org` (admin routes, redirect limbo). Read it in JSX (a reactive
 * context) so links re-render when the org changes.
 */
export { setUrlOrg, urlOrg };

/**
 * Prefix an app path with the org segment: `orgHref('/repos')` →
 * `/org/<login>/repos`. Falls back to the bare path when no org is in scope
 * (e.g. links rendered on the admin routes) — the legacy-redirect route then
 * lands the click in the user's active org.
 */
export function orgHref(path: string, org: string | null = urlOrg()): string {
  if (org === null) return path;
  const base = `${ORG_PREFIX}${encodeURIComponent(org)}`;
  return path === '/' ? base : `${base}${path}`;
}

/** `pathname`'s section past its `/org/<login>` prefix — the shared half `orgSwitchHref` and `orgRouteSection` both need. `''` for a bare `/org/<login>`. */
function rawSection(pathname: string): string {
  const rest = pathname.slice(ORG_PREFIX.length);
  const idx = rest.indexOf('/');
  return idx === -1 ? '' : rest.slice(idx);
}

/**
 * Swap the org segment of `pathname`, keeping the section (`/org/a/fleet` →
 * `/org/b/fleet`) — the switcher's navigation target. A pathname without an
 * org segment lands on the new org's home.
 */
export function orgSwitchHref(pathname: string, nextOrg: string): string {
  if (!pathname.startsWith(ORG_PREFIX)) return orgHref('/', nextOrg);
  return `${ORG_PREFIX}${encodeURIComponent(nextOrg)}${rawSection(pathname)}`;
}

/**
 * `pathname`'s section past its org segment, normalized to a `RouteEntry.path`
 * (`/` for the bare org root, `/terminal` for `/org/<login>/terminal`) — what
 * `AppShell` matches a route's own `path` against to answer "is the CURRENT
 * screen `bleed`". `null` off `/org/…` (admin routes, redirect limbo), where
 * there is no org-scoped route to be bleed or not.
 */
export function orgRouteSection(pathname: string): string | null {
  if (!pathname.startsWith(ORG_PREFIX)) return null;
  const section = rawSection(pathname);
  return section === '' ? '/' : section;
}

/**
 * A server-minted app path (a notification's `deep_link`) scoped to `org`.
 * Producers emit org-less paths (`/fleet`, `/repos/<name>`), and a bare path
 * lands through the legacy catch-all in the SESSION's default org — not the org
 * the notification was read under. A path that already names an org is kept.
 */
export function orgDeepLink(path: string, org: string | null = urlOrg()): string {
  return path.startsWith(ORG_PREFIX) ? path : orgHref(path, org);
}
